import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Center,
  Container,
  HStack,
  Heading,
  Select,
  Spinner,
  Text,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import AdminLayout from '../../layouts/AdminLayout';
import { adminGetVisualData, VisualData, adminUpdateWinner, adminListPrizes, adminSetWinnerPrize, SweepstakePrize } from '../../services/sweepstakes';
import { usePublicSettings } from '../../hooks/usePublicSettings';

const SweepstakeVisualPage: React.FC = () => {
  const { id } = useParams();
  const toast = useToast();
  const [data, setData] = useState<VisualData | null>(null);
  const [loading, setLoading] = useState(true);
  const [variant, setVariant] = useState<'roulette'>('roulette');
  const [theme, setTheme] = useState<'dark' | 'light'>('dark');
  const [confettiOn, setConfettiOn] = useState<boolean>(true);
  const [soundOn, setSoundOn] = useState<boolean>(true);
  const [revealIndex, setRevealIndex] = useState(0); // which winner we are revealing next
  const [playing, setPlaying] = useState(false);
  const [currentIdx, setCurrentIdx] = useState(0); // cycler index
  const timerRef = useRef<number | null>(null);
  const wheelRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const wheelAngleRef = useRef<number>(0);
  const [wheelAngle, setWheelAngle] = useState<number>(0);
  const imgCacheRef = useRef<Record<number, HTMLImageElement>>({});
  const [prizes, setPrizes] = useState<SweepstakePrize[]>([]);

  // Roulette scroller state
  const railRef = useRef<HTMLDivElement | null>(null);
  const [stripItems, setStripItems] = useState<typeof entries>([]);
  const [scrollPx, setScrollPx] = useState<number>(0);
  const [rouletteKey, setRouletteKey] = useState<number>(0); // force re-render/reflow per run
  const [weightingOn, setWeightingOn] = useState<boolean>(true);
  const [speed, setSpeed] = useState<'slow'|'normal'|'fast'>('normal');
  const [drama, setDrama] = useState<number>(3);
  const [transitionMs, setTransitionMs] = useState<number>(4600);

  const entries = data?.entries || [];
  const winners = data?.winners || [];
  const { data: publicSettings } = usePublicSettings();
  const clubLogo = publicSettings?.club_logo_url || '';
  const primary = (publicSettings?.primary_color || '#1e3a8a').trim();

  const targetUserId = winners[revealIndex]?.user_id;
  const targetIndex = useMemo(() => entries.findIndex(e => e.user_id === targetUserId), [entries, targetUserId]);

  // Simple beep
  const beep = () => {
    if (!soundOn) return;
    try {
      const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.connect(g); g.connect(ctx.destination);
      o.type = 'triangle'; o.frequency.value = 880;
      g.gain.value = 0.001; // soft
      o.start();
      g.gain.exponentialRampToValueAtTime(0.5, ctx.currentTime + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.18);
      o.stop(ctx.currentTime + 0.2);
    } catch {}
  };

  const fireConfetti = () => {
    if (!confettiOn) return;
    const host = document.getElementById('visual-host');
    if (!host) return;
    const N = 80;
    for (let i = 0; i < N; i++) {
      const d = document.createElement('div');
      d.className = 'confetti';
      const size = 6 + Math.random() * 6;
      d.style.position = 'absolute';
      d.style.left = (10 + Math.random() * 80) + '%';
      d.style.top = '0%';
      d.style.width = `${size}px`;
      d.style.height = `${size * (0.5 + Math.random())}px`;
      d.style.background = `hsl(${Math.floor(Math.random() * 360)}, 80%, 60%)`;
      d.style.opacity = '0.9';
      d.style.transform = `translate(-50%,-50%) rotate(${Math.random() * 360}deg)`;
      d.style.borderRadius = '1px';
      d.style.pointerEvents = 'none';
      d.style.animation = `fall ${1.5 + Math.random() * 1.5}s ease-out forwards`;
      host.appendChild(d);
      setTimeout(() => { if (d.parentNode) d.parentNode.removeChild(d); }, 3500);
    }
  };

  const hexToRgb = (hex: string): {r:number; g:number; b:number} | null => {
    const h = hex.replace('#','').trim();
    if (![3,6].includes(h.length)) return null;
    const n = h.length === 3 ? h.split('').map(c=>c+c).join('') : h;
    const r = parseInt(n.slice(0,2),16), g = parseInt(n.slice(2,4),16), b = parseInt(n.slice(4,6),16);
    if ([r,g,b].some(x=>Number.isNaN(x))) return null; return { r,g,b };
  };
  const rgbToHsl = (r:number,g:number,b:number): [number,number,number] => {
    r/=255; g/=255; b/=255; const max=Math.max(r,g,b), min=Math.min(r,g,b); let h=0,s=0,l=(max+min)/2;
    if (max!==min){ const d=max-min; s=l>0.5? d/(2-max-min) : d/(max+min);
      switch(max){ case r: h=(g-b)/d+(g<b?6:0); break; case g: h=(b-r)/d+2; break; case b: h=(r-g)/d+4; break; }
      h/=6;
    }
    return [h,s,l];
  };
  const hslToCss = (h:number,s:number,l:number) => `hsl(${Math.round(h*360)}, ${Math.round(s*100)}%, ${Math.round(l*100)}%)`;

  const startCycler = () => {
    if (!entries.length || revealIndex >= winners.length) return;
    setPlaying(true);
    let speed = 50; // ms
    let steps = 0;
    const maxWarmup = 40;
    const decelStart = maxWarmup + 40;
    const slowMax = decelStart + 80;
    const loop = () => {
      setCurrentIdx((idx) => (idx + 1) % entries.length);
      steps++;
      // warmup constant speed, then decelerate
      if (steps < maxWarmup) {
        timerRef.current = window.setTimeout(loop, speed);
      } else if (steps < decelStart) {
        speed += 5; // slight slow
        timerRef.current = window.setTimeout(loop, speed);
      } else if (steps < slowMax) {
        speed += 15;
        timerRef.current = window.setTimeout(loop, speed);
      } else {
        // Try to land on target
        const idx = (currentIdx + 1) % entries.length;
        setCurrentIdx(idx);
        const landing = idx === targetIndex;
        if (!landing) {
          speed += 30;
          timerRef.current = window.setTimeout(loop, speed);
        } else {
          // reveal done
          setPlaying(false);
          setRevealIndex((i) => i + 1);
          beep(); fireConfetti();
        }
      }
    };
    loop();
  };

  // Draw segmented wheel
  const drawWheel = () => {
    const canvas = canvasRef.current; if (!canvas) return;
    const ctx = canvas.getContext('2d'); if (!ctx) return;
    const W = canvas.width = 440; const H = canvas.height = 440; // fixed size
    const cx = W/2, cy = H/2, r = Math.min(W, H)/2 - 8;
    ctx.clearRect(0,0,W,H);
    const n = Math.max(entries.length, 1);
    const angle = (Math.PI*2)/n;
    const base = hexToRgb(primary) || { r: 30, g: 58, b: 138 };
    const [bh, bs, bl] = rgbToHsl(base.r, base.g, base.b);
    for (let i=0;i<n;i++){
      const a0 = i*angle, a1 = a0 + angle;
      ctx.beginPath(); ctx.moveTo(cx,cy);
      ctx.arc(cx,cy,r,a0,a1,false); ctx.closePath();
      const l = theme==='dark' ? (0.30 + 0.15 * ((i%2)?1:0)) : (0.55 + 0.10 * ((i%2)?1:0));
      const s = Math.min(0.9, bs + 0.1);
      const h = (bh + (i/n)*0.08) % 1; // slight hue drift for variety
      ctx.fillStyle = hslToCss(h, s, l);
      ctx.fill();
      // border
      ctx.strokeStyle = theme==='dark' ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.1)';
      ctx.lineWidth = 2; ctx.stroke();
      // label
      const label = (entries[i]?.display_name || '').trim();
      if (label){
        ctx.save();
        ctx.translate(cx,cy);
        ctx.rotate(a0 + angle/2);
        ctx.textAlign = 'right'; ctx.fillStyle = 'white'; ctx.font = '700 13px system-ui, sans-serif';
        const text = label.length>18? (label.slice(0,17)+'…') : label;
        ctx.fillText(text, r - 10, 5);
        ctx.restore();
      }
      // avatar (small circle near rim)
      const avatarUrl = entries[i]?.avatar_url;
      if (avatarUrl) {
        let img = imgCacheRef.current[i];
        const drawImg = (im: HTMLImageElement) => {
          ctx.save();
          const mid = a0 + angle/2;
          const ar = r - 36;
          const ax = cx + Math.cos(mid) * ar;
          const ay = cy + Math.sin(mid) * ar;
          const sz = 26;
          ctx.beginPath(); ctx.arc(ax, ay, sz/2, 0, Math.PI*2); ctx.closePath(); ctx.clip();
          ctx.drawImage(im, ax - sz/2, ay - sz/2, sz, sz);
          ctx.restore();
        };
        if (img && img.complete) drawImg(img);
        else {
          img = new Image(); img.crossOrigin = 'anonymous'; img.src = avatarUrl; img.onload = () => { drawImg(img!); };
          imgCacheRef.current[i] = img;
        }
      }
    }
    // center circle
    ctx.beginPath(); ctx.arc(cx,cy,20,0,Math.PI*2); ctx.fillStyle = theme==='dark'? '#111':'#eee'; ctx.fill();
  };

  const startWheel = () => {
    if (!entries.length || revealIndex >= winners.length) return;
    const n = entries.length; if (!n) return;
    const target = targetIndex;
    if (target < 0) { startCycler(); return; }
    setPlaying(true);
    // Compute final angle so pointer at top hits target center
    const per = 360 / n;
    const center = target * per + per/2;
    const spins = 4 + Math.floor(Math.random()*3); // 4-6 spins
    const final = spins*360 + (360 - center);
    wheelAngleRef.current = final;
    setWheelAngle(final);
    // After transition ends (~4s), reveal winner
    const duration = 4200;
    window.setTimeout(() => {
      setPlaying(false);
      setRevealIndex(i=>i+1);
      beep(); fireConfetti();
    }, duration);
  };

  const startRoulette = () => {
    if (!entries.length || revealIndex >= winners.length) return;
    const target = targetIndex;
    if (target < 0) { startCycler(); return; }
    setPlaying(true);
    // Build a long strip of avatars.
    // Pool selection: when weighting is ON, keep duplicates from entries; when OFF, use unique users (flat odds).
    const uniqueMap = new Map<number, typeof entries[0]>();
    for (const e of entries) { if (!uniqueMap.has(e.user_id)) uniqueMap.set(e.user_id, e); }
    const pool = weightingOn ? entries : Array.from(uniqueMap.values());
    const total = Math.max(80, Math.min(240, pool.length * 4));
    const rnd = (n: number) => Math.floor(Math.random() * n);
    const list: typeof entries = [] as any;
    for (let i = 0; i < total - 10; i++) {
      list.push(pool[rnd(pool.length)]);
    }
    // Ensure target appears near the end, centered under pointer at stop
    const targetEntry = entries[target];
    const tailPad = 6;
    for (let i = 0; i < tailPad - 1; i++) list.push(pool[rnd(pool.length)]);
    list.push(targetEntry);
    setStripItems(list);

    // Next tick measure viewport + compute scroll distance
    window.requestAnimationFrame(() => {
      try {
        const host = document.getElementById('visual-host');
        const rail = railRef.current;
        if (!host || !rail) { setPlaying(false); return; }
        const viewport = host.getBoundingClientRect();
        const cardW = 72; // width incl. margin approx
        const gap = 8;
        const itemSize = cardW + gap;
        const landingIndex = list.length - 1; // last item is target
        const centerOffset = Math.max(0, (viewport.width - cardW) / 2);
        const distance = landingIndex * itemSize - centerOffset;
        // Prime initial position
        setScrollPx(0);
        setRouletteKey((k) => k + 1);
        // Start animation in next frame
        setTimeout(() => {
          // Add extra laps based on drama level (1..5)
          const dramaFactor = Math.max(0, Math.min(5, Number(drama) || 3));
          const extra = viewport.width * dramaFactor + rnd(viewport.width);
          setScrollPx(distance + extra);
          // Duration based on speed
          const mul = speed === 'slow' ? 1.25 : (speed === 'fast' ? 0.75 : 1.0);
          const duration = Math.round(4600 * mul);
          setTransitionMs(duration);
          window.setTimeout(() => {
            setPlaying(false);
            setRevealIndex((i) => i + 1);
            beep(); fireConfetti();
          }, duration + 50);
        }, 40);
      } catch {
        setPlaying(false);
      }
    });
  };

  const onStart = () => { startRoulette(); };

  // Reveal All logic
  const [revealAll, setRevealAll] = useState(false);
  useEffect(() => {
    if (!revealAll) return;
    if (!playing && revealIndex < winners.length) {
      const t = window.setTimeout(() => onStart(), 400);
      return () => window.clearTimeout(t);
    }
    if (revealIndex >= winners.length) {
      setRevealAll(false);
    }
  }, [revealAll, playing, revealIndex, winners.length, variant]);

  const onFullscreen = () => {
    const el = document.getElementById('visual-host');
    if (el && el.requestFullscreen) el.requestFullscreen().catch(()=>{});
  };

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        setLoading(true);
        const res = await adminGetVisualData(Number(id));
        if (!active) return;
        setData(res);
        setVariant('roulette');
        try { const list = await adminListPrizes(Number(id)); if (active) setPrizes(list); } catch {}
      } catch (e: any) {
        toast({ status: 'error', title: 'Nelze načíst data vizualizace' });
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; if (timerRef.current) window.clearTimeout(timerRef.current); };
  }, [id]);

  // Wheel variant removed – no canvas redraw needed

  if (loading) {
    return (
      <AdminLayout>
        <Container maxW="6xl" py={8}><Spinner /></Container>
      </AdminLayout>
    );
  }
  if (!data) {
    return (
      <AdminLayout>
        <Container maxW="6xl" py={8}><Text>Žádná data</Text></Container>
      </AdminLayout>
    );
  }

  const shownWinners = winners.slice(0, revealIndex);

  return (
    <AdminLayout>
      <Container maxW="6xl" py={6}>
        <HStack justify="space-between" mb={3}>
          <Heading size="lg">Vizualizace – {data.sweepstake.title}</Heading>
          <HStack>
            <Button as={RouterLink} to="/admin/sweepstakes" variant="outline">Zpět</Button>
            <Button onClick={onFullscreen} variant="outline">Fullscreen</Button>
          </HStack>
        </HStack>
        <HStack mb={4} spacing={4}>
          <Select value={theme} onChange={(e)=>setTheme(e.target.value as any)} maxW="200px">
            <option value="dark">Tmavé pozadí</option>
            <option value="light">Světlé pozadí</option>
          </Select>
          <Select value={speed} onChange={(e)=>setSpeed(e.target.value as any)} maxW="200px">
            <option value="slow">Rychlost: Pomalá</option>
            <option value="normal">Rychlost: Normální</option>
            <option value="fast">Rychlost: Rychlá</option>
          </Select>
          <Select value={String(drama)} onChange={(e)=>setDrama(Number(e.target.value)||3)} maxW="200px">
            <option value="1">Drama: 1</option>
            <option value="2">Drama: 2</option>
            <option value="3">Drama: 3</option>
            <option value="4">Drama: 4</option>
            <option value="5">Drama: 5</option>
          </Select>
          <HStack>
            <Button size="sm" variant={confettiOn? 'solid':'outline'} onClick={()=>setConfettiOn(v=>!v)}>{confettiOn? 'Konfety: Zap' : 'Konfety: Vyp'}</Button>
            <Button size="sm" variant={soundOn? 'solid':'outline'} onClick={()=>setSoundOn(v=>!v)}>{soundOn? 'Zvuk: Zap' : 'Zvuk: Vyp'}</Button>
            <Button size="sm" variant={weightingOn? 'solid':'outline'} onClick={()=>setWeightingOn(v=>!v)}>{weightingOn? 'Vážit účastí: Zap' : 'Vážit účastí: Vyp'}</Button>
          </HStack>
          <Button colorScheme="blue" onClick={onStart} isDisabled={playing || revealIndex >= winners.length}>
            {revealIndex >= winners.length ? 'Všichni výherci odhaleni' : (playing ? 'Probíhá…' : 'Start')}
          </Button>
          <Button variant="outline" onClick={()=>setRevealAll(true)} isDisabled={playing || revealIndex >= winners.length}>Odhalit všechny</Button>
          <Button variant="outline" onClick={()=>{
            // CSV export: user_id, name, prize_name
            const rows = winners.map((w:any)=>{
              const e = entries.find(x=>x.user_id===w.user_id);
              return [w.user_id, (e?.display_name||'').replaceAll('"','""'), (w.prize_name||'').replaceAll('"','""')];
            });
            const csv = ['user_id,name,prize'].concat(rows.map(r=>`${r[0]},"${r[1]}","${r[2]}"`)).join('\n');
            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a'); a.href=url; a.download=`soutez_${id}_vitezove.csv`; a.click(); URL.revokeObjectURL(url);
          }}>Export CSV</Button>
          <Text color="gray.500">Výherci: {revealIndex}/{winners.length}</Text>
        </HStack>

        <Box id="visual-host" borderWidth="1px" borderRadius="md" p={4} minH="400px" position="relative" overflow="hidden" bg={theme==='dark' ? 'black' : 'white'} color={theme==='dark' ? 'white' : 'black'}>
          <Center h="380px" flexDir="column">
            <Box position="relative" w="100%" maxW="960px" h="220px">
              {/* pointer */}
              <Box position="absolute" left="50%" top="10px" transform="translateX(-50%)" zIndex={3}
                borderLeft="10px solid transparent" borderRight="10px solid transparent" borderBottom={`16px solid ${theme==='dark'?'#e2e8f0':'#1a202c'}`} />
              {/* center divider */}
              <Box pointerEvents="none" position="absolute" left="50%" top={42} bottom={22} width="2px" transform="translateX(-1px)" zIndex={2}
                style={{ background: theme==='dark' ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.2)' }} />
              {/* scrolling rail */}
              <Box key={rouletteKey} position="absolute" left={0} right={0} top={40} bottom={20} overflow="hidden" borderRadius="md" borderWidth="1px" bg={theme==='dark'?'#0b0b0b':'#f9fafb'}>
                <div ref={railRef} style={{ display:'flex', alignItems:'center', gap:8, padding:'8px', transform:`translateX(-${scrollPx}px)`, transition: playing? `transform ${transitionMs/1000}s cubic-bezier(.2,.8,.2,1)` : undefined }}>
                  {stripItems.map((it, idx) => (
                    <div key={idx} style={{ width:72, height:72, borderRadius:12, background: theme==='dark'?'#111':'#fff', boxShadow: theme==='dark'?'0 1px 2px rgba(255,255,255,0.08)':'0 1px 2px rgba(0,0,0,0.08)', display:'flex', alignItems:'center', justifyContent:'center', overflow:'hidden', border:'1px solid rgba(0,0,0,0.08)' }}>
                      {it?.avatar_url ? (
                        // eslint-disable-next-line jsx-a11y/alt-text
                        <img src={it.avatar_url} style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                      ) : (
                        <span style={{ fontSize:24, fontWeight:800 }}>{(it?.display_name||'?').slice(0,1)}</span>
                      )}
                    </div>
                  ))}
                </div>
                {/* fade edges */}
                <Box pointerEvents="none" position="absolute" left={0} top={0} bottom={0} width="120px" zIndex={1}
                  style={{ background: theme==='dark' ? 'linear-gradient(to right, rgba(0,0,0,0.85), rgba(0,0,0,0))' : 'linear-gradient(to right, rgba(255,255,255,0.95), rgba(255,255,255,0))' }} />
                <Box pointerEvents="none" position="absolute" right={0} top={0} bottom={0} width="120px" zIndex={1}
                  style={{ background: theme==='dark' ? 'linear-gradient(to left, rgba(0,0,0,0.85), rgba(0,0,0,0))' : 'linear-gradient(to left, rgba(255,255,255,0.95), rgba(255,255,255,0))' }} />
              </Box>
              <Text mt={4} opacity={0.8} textAlign="center">Ruleta</Text>
            </Box>
          </Center>
        </Box>

        <VStack align="stretch" mt={6} spacing={2}>
          <Heading size="md">Odhalení</Heading>
          {shownWinners.length === 0 && <Text color="gray.500">Zatím žádný výherce</Text>}
          {shownWinners.map((w, idx) => {
            const e = entries.find(x => x.user_id === w.user_id);
            const [status, setStatus] = [w.claim_status || 'pending', undefined];
            return (
              <HStack key={`${w.user_id}-${idx}`} spacing={8} borderWidth="1px" borderRadius="md" p={3} align="center">
                <HStack spacing={3} flex={1}>
                  {e?.avatar_url && (<img src={e.avatar_url} alt="avatar" style={{ width: 36, height: 36, borderRadius: '50%' }} />)}
                  <Text fontWeight="700">{e?.display_name || `Uživatel #${w.user_id}`}</Text>
                  {w.prize_name && <Text color="gray.500">— {w.prize_name}</Text>}
                </HStack>
                <HStack>
                  <Select size="sm" value={w.claim_status || 'pending'} onChange={async (ev)=>{
                    const val = ev.target.value as 'pending'|'claimed'|'delivered';
                    try {
                      if (w.id) await adminUpdateWinner(Number(id), w.id, { claim_status: val });
                      // update local state without refetch
                      setData((prev)=> prev ? ({ ...prev, winners: prev.winners.map((x,i)=> i===idx ? { ...x, claim_status: val } : x) }) : prev);
                    } catch { toast({ status: 'error', title: 'Nelze uložit stav' }); }
                  }} maxW="160px">
                    <option value="pending">čeká</option>
                    <option value="claimed">vyzvednuto</option>
                    <option value="delivered">předáno</option>
                  </Select>
                </HStack>
              </HStack>
            );
          })}
        </VStack>
      </Container>
      <style>{`
      @keyframes fall { 
        0% { transform: translate(-50%,-50%) rotate(0deg); top: 0%; opacity: 1 }
        100% { transform: translate(-50%, 520px) rotate(360deg); top: 100%; opacity: 0.2 }
      }
      `}</style>
    </AdminLayout>
  );
};

export default SweepstakeVisualPage;
